<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, ref, onMounted, onActivated } from 'vue';

import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const servicesList = computed(() => getters['services/getServices'].value);
const uiFlags = computed(() => getters['services/getUIFlags'].value);
const { isAdmin } = useAdmin();

const loading = ref({});

const loadServices = () => {
  store.dispatch('services/get', false);
};

onMounted(() => {
  loadServices();
});

onActivated(() => {
  loadServices();
});

const deleteService = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('services/delete', id);
    useAlert(t('SERVICES_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.message ||
      t('SERVICES_SETTINGS.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[id] = false;
  }
};

const getTeamName = teamId => {
  const teams = getters['teams/getTeams'].value;
  const team = teams.find(teamItem => teamItem.id === teamId);
  const name = team ? team.name : '-';
  return name.length > 25 ? `${name.slice(0, 25)}...` : name;
};

const getSlaName = slaId => {
  const slas = getters['sla/getSLA'].value;
  const sla = slas.find(s => s.id === slaId);
  return sla ? sla.name : '-';
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('SERVICES_SETTINGS.HEADER')"
      :description="$t('SERVICES_SETTINGS.DESCRIPTION')"
      :link-text="$t('SERVICES_SETTINGS.LEARN_MORE')"
    >
      <template #actions>
        <router-link v-if="isAdmin" :to="{ name: 'settings_services_new' }">
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('SERVICES_SETTINGS.NEW_SERVICE')"
          />
        </router-link>
      </template>
    </BaseSettingsHeader>
    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.isFetching"
        :message="$t('SERVICES_SETTINGS.LOADING')"
      />
      <p
        v-else-if="!servicesList.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('SERVICES_SETTINGS.LIST.404') }}
      </p>

      <table v-else class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.NAME') }}
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.TEAM') }}
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.SLA') }}
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.SUBSERVICES') }}
            </th>
            <th class="py-3 text-left text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.STATUS') }}
            </th>
            <th class="py-3 text-right text-sm font-medium text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.LIST.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="service in servicesList" :key="service.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="block font-medium">{{ service.name }}</span>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ service.description }}
              </p>
            </td>
            <td class="py-4">
              <span class="text-sm">{{ getTeamName(service.team_id) }}</span>
            </td>
            <td class="py-4">
              <span class="text-sm">{{
                getSlaName(service.sla_policy_id)
              }}</span>
            </td>
            <td class="py-4">
              <span class="text-sm">{{
                service.sub_services?.length || 0
              }}</span>
            </td>
            <td class="py-4">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="
                  service.active
                    ? 'bg-n-teal-2 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  service.active
                    ? $t('SERVICES_SETTINGS.STATUS.ACTIVE')
                    : $t('SERVICES_SETTINGS.STATUS.INACTIVE')
                }}
              </span>
            </td>
            <td class="py-4 flex justify-end gap-1">
              <router-link
                v-if="isAdmin"
                :to="{
                  name: 'settings_services_edit',
                  params: { accountId: route.params.accountId, id: service.id },
                }"
              >
                <Button
                  v-tooltip.top="$t('SERVICES_SETTINGS.LIST.EDIT_SERVICE')"
                  icon="i-lucide-pencil"
                  slate
                  xs
                  faded
                />
              </router-link>

              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('SERVICES_SETTINGS.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[service.id]"
                @click="deleteService(service)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
